/**
 * ScanMapPanel — the list | map split that BOTH scan surfaces render.
 *
 * VRG-491. The Analytics card (`ScansByLocation`) and the certificate page's Scans tab
 * had converged on the same PICTURE — rows on the left, a Google map on the right,
 * selection flowing both ways — by being written twice, six months apart. They then
 * drifted in every detail that was not the picture: the certificate page hand-rolled its
 * resizer on raw `mousedown`/`mousemove` (no keyboard, no `aria`), had no collapse, no
 * list header, no skeleton (§19), no clustering, and remounted the whole Google map on
 * every drag-end — the exact teardown-flash the Analytics card spent six reported bugs
 * removing. §36 says the second hand-roll is a shared component; this is that component,
 * extracted from the Analytics side because that is where the fixes actually happened.
 *
 * ## What lives here, and what deliberately does not
 *
 * HERE: everything about the SPLIT and the MAP WIRING — the resizer and its px bounds,
 * the collapse rail, the count strip, the footprint-parity skeleton, the pins/heat
 * toggle, and the whole no-remount/`relayoutNonce` machinery whose reasoning the long
 * comments below record.
 *
 * NOT HERE: what a ROW looks like, where the points came from, and what the tooltip
 * says. Those are genuinely per-surface — `IScanMapPoint` carries a product and no user,
 * a certificate scan carries a user and one product — so the list arrives as a `list`
 * node and the points arrive as `MapItem[]` the caller has already built. A shared
 * component that also owned the row would have to branch on which page it was on, which
 * is the shape this extraction exists to remove.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react'

import {
  CONTROL_HEIGHT,
  Map,
  Skeleton,
  hideScrollbar,
  type MapDrawMode,
  type MapItem,
  type MapRenderMode,
  type MapRingPoint,
} from '@repo/ui'

import { isChromeTransitioning } from 'config'
import { ResizableSplit } from 'components/layout/ResizableSplit'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'

export type ScanMapRenderMode = MapRenderMode

/**
 * Below this many located points, heat mode is DISABLED (visible, dimmed, tooltipped —
 * §46) rather than hidden. RECOMMENDATION, not a hard product rule — see
 * `ScanMapRenderModeToggle` for the honest tradeoff and why this landed on
 * disable-with-tooltip over hiding the control outright.
 */
export const HEAT_MODE_MIN_POINTS = 200

/**
 * Height of a chip floating over the map canvas, and of every control that IS one.
 *
 * `CONTROL_HEIGHT` (40) verbatim, named here because two different files draw a chip on
 * this canvas and they have to agree: the render-mode chip below, and the certificate
 * map's Journey chip (`CertificatePage`'s `Scans.tsx`, a shared 40px `Button` passed in
 * through `canvasTopLeft`). They sit at the same y on the same surface, so a 4px
 * disagreement reads immediately as one of them being wrong — which is what shipped, at
 * 36 against 36 with a 40px control bursting out of one of them.
 */
const MAP_CHIP_HEIGHT = CONTROL_HEIGHT

/**
 * The `ViewToggle` INSIDE that chip — deliberately smaller than the chip itself.
 *
 * 40 - 32 = 8, split evenly, is the inset the toggle's hover wash needs so it reads as a
 * highlight sitting within the chip rather than a block painted across its edges. See the
 * `height` prop's own docblock on `ViewToggle` for why the control's height belongs to its
 * neighbour rather than to the control.
 */
const MAP_CHIP_CONTROL_HEIGHT = 32

/**
 * Minimum container-width change, in px, that justifies re-laying-out the map.
 *
 * Wider than any platform's scrollbar (~15px macOS, ~17px Windows) so a scrollbar
 * appearing or disappearing can never trigger one, and well under any genuine resize (a
 * split drag, the app sidebar collapsing, a window change), which must still re-tile.
 * See the observer's own note for the bug this closes.
 */
const RESIZE_RELAYOUT_MIN_DELTA_PX = 24

/**
 * How long to wait after the last observed width change before re-tiling, when nothing
 * has told us the resize has a known end (a window drag, a zoom change).
 */
const RESIZE_RELAYOUT_DEBOUNCE_MS = 180

/**
 * The same wait when a chrome transition HAS been announced (`appChromeTransition`).
 *
 * Only has to outlast the gap between two frames of that transition — ResizeObserver
 * delivery is not guaranteed every frame, so a tail shorter than about two frames would
 * re-tile mid-animation and then again at the end. ~2.5 frames at 60fps.
 */
const RELAYOUT_ANNOUNCED_SETTLE_MS = 40

/** Default height of the list | map split. One constant so the skeleton matches it exactly (§19). */
export const SCAN_MAP_PANEL_HEIGHT = 600

/**
 * DEFAULT width of the scan list column. In PIXELS, not a percentage — see
 * `ResizableSplit`'s header for why a content column's bound is px: the card's own width
 * changes with its grid span and with the Components panel, so one percentage is a
 * comfortable 400px in one position and an unreadable 240px in another.
 *
 * 400, not 340: measured at 1280x800, a 340 column left the product title 79px after the
 * 44px thumbnail, the status chip and the relative time had taken their share — narrow
 * enough that every title was an ellipsis. 400 gives it ~140 and still leaves the map
 * ~650.
 */
export const SCAN_MAP_LIST_WIDTH = 400
/**
 * Resize bounds.
 *
 * The minimum is the width at which a row is still a row: 280 keeps the 44px thumbnail, a
 * two-or-three-word title and the relative time on one line. Below that the title is an
 * ellipsis on every row and the list stops being readable, which is the point at which
 * the honest control is COLLAPSE, not a narrower list — so the floor is set where the
 * collapse takes over rather than letting the user drag into an unusable state.
 *
 * The maximum stops the list from taking the map's job. At 560 the map still has ~590px
 * at a 1280 full-width span, which is enough for a fitted world view.
 */
const LIST_MIN_WIDTH = 280
const LIST_MAX_WIDTH = 560
/**
 * Width of the collapsed rail.
 *
 * The list collapses to a RAIL, not to nothing. A zero-width collapse would have to float
 * its own re-open control over the map canvas, where it competes with Google's controls
 * and with the pins underneath it; a 44px rail gives the control a home that is
 * unambiguously part of the panel it re-opens, and it costs the map 44px instead of the
 * 400 it just got back. Same pattern as a collapsed IDE sidebar.
 */
const LIST_RAIL_WIDTH = 44

/**
 * Pins vs heat — a RENDERING question, deliberately separate from any data-selection
 * control a surface puts beside it (VERA-260 task 4).
 *
 * Text labels, not icons: "Pins" and "Heat" DO have conventional glyphs available
 * (`MapPinIcon`, `FlameIcon`), but an icon-form toggle beside a text-form one in the same
 * header reads as two different controls at two different weights when they are meant to
 * read as siblings.
 */
export const ScanMapRenderModeToggle = ({
  value,
  onChange,
  pointCount,
  unavailable = false,
}: {
  value: ScanMapRenderMode
  onChange: (next: ScanMapRenderMode) => void
  /** Located points in the current set — drives the low-volume disable below. */
  pointCount: number
  /**
   * Heat could not render — the shared `Map` renders it via deck.gl's `HeatmapLayer`, and
   * this fires if deck.gl fails to initialise or render (no WebGL context, a network
   * failure loading the lazy-imported renderer, or any other error). Only discoverable by
   * trying, so it is reported up from the shared `Map` rather than predicted here.
   *
   * Ranked ABOVE the low-volume reason below: "there is not enough data yet" invites the
   * user to come back with more scans, which would be a lie when the renderer is gone.
   */
  unavailable?: boolean
}) => {
  const tooFewForHeat = !unavailable && pointCount > 0 && pointCount < HEAT_MODE_MIN_POINTS

  /**
   * RECOMMENDATION: disable-with-tooltip, not hide, below `HEAT_MODE_MIN_POINTS`.
   *
   * The honest limit this is answering: at low volume heat genuinely looks WORSE than
   * pins — a couple of diffuse blobs conveying less than a handful of clickable dots
   * would. That argues for hiding it. But §46's own axis is who RESOLVES the block: hide
   * is for a state the user resolves by acting; disable+tooltip is for a state nobody in
   * the moment can just fix. Not having a few hundred located scans yet is the second
   * kind — the reader cannot make more scans happen by clicking something else, and OFTEN
   * the honest reason is "not enough time has passed" rather than "wrong filters", which
   * is worth SAYING rather than hiding. A hidden option also teaches nothing: a reader who
   * never sees "Heat" exist has no idea the capability is coming as their data grows.
   */
  const options: readonly ViewToggleOption<ScanMapRenderMode>[] = [
    { value: 'pins', label: 'Pins' },
    {
      value: 'heat',
      label: 'Heat',
      disabled: unavailable || tooFewForHeat,
      // Order matters. "Unavailable" outranks "not enough data": telling someone to come
      // back with more scans would be a lie when the renderer itself is gone.
      disabledReason: unavailable
        ? 'Heat maps could not load in this browser. Pins show every scan individually.'
        : tooFewForHeat
          ? `Not enough located ${pointCount === 1 ? 'scan' : 'scans'} yet to be useful (${pointCount} of a recommended ${HEAT_MODE_MIN_POINTS}+). Pins still show every one individually.`
          : undefined,
    },
  ]

  return (
    <ViewToggle
      options={options}
      value={value}
      onChange={onChange}
      ariaLabel="Map render mode"
      /*
        32, not the default 40 — this toggle is the one call site that sits INSIDE a drawn
        container. The chip below owns the 40 (so it matches the certificate map's Journey
        chip, a 40px `Button`), which leaves the control 4px of inset on each side for its
        hover wash. At 40 the wash had none: a 40px button in a 36px chip overflowed it top
        and bottom and the grey visibly crossed the chip's top edge.
      */
      height={MAP_CHIP_CONTROL_HEIGHT}
    />
  )
}

export interface ScanMapPanelProps {
  /** Points to plot. Built by the caller — see the file header on why. */
  items: MapItem[]
  activeId?: string
  onActiveIdChange: (id: string | undefined) => void

  /** The surface's own row list. Rows must carry `data-scan-id` for selection sync. */
  list: React.ReactNode
  /** How many rows `list` is rendering — the header strip's count. */
  count: number
  /** Singular noun for the count and the collapse labels. */
  noun?: string

  apiKey: string
  /**
   * The split's height. A number is px (the map gets `height - 2` for the container's
   * border); `'100%'` fills whatever the parent gives it.
   */
  height: number | string
  /** FIRST LOAD only (§19) — see `ScansByLocation`'s `hasEverLoaded` for why not `isLoading`. */
  loading?: boolean
  /** Overlay copy when nothing can be plotted. */
  emptyMessage: string
  errorMessage?: string

  /** See `MapProps.fitSignature` / `MapProps.suppressFit`. */
  fitSignature?: string
  suppressFit?: boolean
  enableClustering?: boolean

  /** CONTROLLED render mode. Omit both and the panel manages it itself. */
  renderMode?: ScanMapRenderMode
  onRenderModeChange?: (next: ScanMapRenderMode) => void
  /** Render the pins/heat chip over the canvas (top-right). */
  showRenderModeToggle?: boolean

  /** See `MapProps.path` — a single subject's ordered journey. Never a cross-subject set. */
  path?: MapRingPoint[]

  /** Area selection, passed straight through to `Map`. */
  drawMode?: MapDrawMode
  drawnRing?: MapRingPoint[] | null
  onDrawComplete?: (ring: MapRingPoint[]) => void
  onDrawCancel?: () => void

  /** A floating control over the map canvas's top-left corner. */
  canvasTopLeft?: React.ReactNode

  /** DOM id on the outer box, so a surface elsewhere can scroll the panel into view. */
  id?: string
  defaultListWidth?: number
}

export const ScanMapPanel = ({
  items,
  activeId,
  onActiveIdChange,
  list,
  count,
  noun = 'scan',
  apiKey,
  height,
  loading = false,
  emptyMessage,
  errorMessage = 'Google Maps API key not configured',
  fitSignature,
  suppressFit,
  enableClustering = true,
  renderMode: renderModeProp,
  onRenderModeChange,
  showRenderModeToggle = true,
  path,
  drawMode,
  drawnRing,
  onDrawComplete,
  onDrawCancel,
  canvasTopLeft,
  id,
  defaultListWidth = SCAN_MAP_LIST_WIDTH,
}: ScanMapPanelProps) => {
  const theme = useTheme()
  const mapStyle = theme.palette.mode === 'dark' ? 'dark' : 'light'

  // `aria-controls` on the collapse toggle has to name a real element, and a page can
  // hold more than one of these panels in principle, so the id is generated rather than a
  // constant that could collide.
  const listRegionId = React.useId()

  /**
   * The split's state — collapsed, and the list's width — is LOCAL and ephemeral.
   *
   * It deliberately does NOT go into any saved view. That state is the per-card GRID
   * layout the canvas owns; how one card arranges its own interior is not part of that
   * contract, and putting it there would mean every saved view carried a field only this
   * component can read.
   */
  const [listCollapsed, setListCollapsed] = useState(false)
  const [listWidth, setListWidth] = useState(defaultListWidth)

  /**
   * ── RENDER MODE — pins vs heat ────────────────────────────────────────────────────
   *
   * CONTROLLED-with-uncontrolled-fallback: a surface that wires
   * `renderMode`/`onRenderModeChange` (e.g. to put the toggle in a card header) drives it
   * from outside; absent that wiring this panel manages it itself, so the canvas chip
   * works without any call-site ceremony.
   */
  const [localRenderMode, setLocalRenderMode] = useState<ScanMapRenderMode>('pins')
  const renderMode = renderModeProp ?? localRenderMode
  const setRenderMode = onRenderModeChange ?? setLocalRenderMode

  /**
   * Heat is UNAVAILABLE, discovered at call time. deck.gl's failure modes (no WebGL
   * context, a network failure loading the lazy-imported renderer) are only discoverable
   * by trying, so `Map` catches them internally and reports here rather than this panel
   * predicting them in advance. Sticky for the session: retrying on every render would
   * re-throw into the same catch on a loop.
   */
  const [heatUnavailable, setHeatUnavailable] = useState(false)
  const handleHeatUnavailable = useCallback(() => {
    setHeatUnavailable(true)
    setRenderMode('pins')
  }, [setRenderMode])

  // Selection flows BOTH ways: clicking a pin has to bring its row into view, or the list
  // silently disagrees with the map whenever the row is below the fold.
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!activeId) return
    const row = listRef.current?.querySelector(`[data-scan-id="${activeId}"]`)
    ;(row as HTMLElement | null)?.scrollIntoView({ block: 'nearest' })
  }, [activeId])

  const handleMarkerClick = useCallback(
    (item: MapItem) => onActiveIdChange(String(item.id)),
    [onActiveIdChange],
  )
  const handleDeselect = useCallback(() => onActiveIdChange(undefined), [onActiveIdChange])

  // ---------------------------------------------------------------------------
  // Re-layout, never remount — Google tiles for its MOUNT-TIME box
  // ---------------------------------------------------------------------------
  //
  // A map whose container settles (or changes) after mount renders ONE tile on a blank
  // field, because Google Maps tiles for the size it was initialised at and does not
  // re-tile on a resize it did not notice.
  //
  // ⚠️ THE `<Map>` BELOW IS DELIBERATELY UNKEYED, and that is the end of a long argument
  // worth recording, because both bugs the Analytics card was reported for most were a
  // `key` firing for something that only needed a re-tile:
  //
  //   * `points.length` in the key remounted the map mid-draw. Applying a ring filters the
  //     set (504 -> ~123), React destroyed and rebuilt the whole map — that teardown is
  //     the FLASH — and the brand-new instance ran its INITIAL fit, which is the ZOOM.
  //     Clearing the ring did the same in reverse. Google syncs markers from the `items`
  //     effect perfectly well without being torn down.
  //   * The width `ResizeObserver` remounted it on an app-sidebar collapse: a visible
  //     teardown that also arrived ~400ms after every other card had finished animating.
  //
  // `relayoutNonce` (`@repo/ui`'s `Map`) replaces all of it: it tells the ALREADY-MOUNTED
  // map to re-read its container and re-tile, with no teardown. It is bumped DIRECTLY at
  // the call sites that change the map's width on purpose (`toggleListCollapsed`,
  // `handleResizeCommit`, `handleListWidthTransitionEnd`) rather than relying on the
  // observer, because `ResizeObserver` fires ZERO times in the CCD preview pane — and so
  // does Google's own internal auto-resize detection, which is why "just let the observer
  // notice" was tried and reverted (it left the map permanently blank there).
  //
  // If a future change genuinely needs a rebuild, add a key for THAT reason and say what
  // it is. Do not reintroduce a general-purpose one.
  const mapBoxRef = useRef<HTMLDivElement>(null)
  const lastWidthRef = useRef<number | null>(null)
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [relayoutNonce, setRelayoutNonce] = useState(0)

  /**
   * "This component just changed the map's width on purpose."
   *
   * Drops the observer's baseline so the next observation — which is the one OUR change
   * produces — becomes the new baseline instead of firing a second relayout on top of the
   * one the caller already made. Ordering is what makes it safe: this runs inside the
   * click / drag-end handler, i.e. before React re-renders and therefore long before
   * layout changes and the observer can see anything.
   */
  const settleOwnResize = useCallback(() => {
    if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
    resizeTimerRef.current = undefined
    lastWidthRef.current = null
  }, [])

  useEffect(() => {
    const el = mapBoxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    // A different element is being observed on each (re)attach, so the "what width did
    // this map mount at" baseline has to start over with it. The FIRST measurement is
    // deliberately swallowed: it is the mount-time box, which the map already tiled for.
    lastWidthRef.current = null
    const observer = new ResizeObserver(entries => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0)
      if (next === 0) return
      if (lastWidthRef.current === null) {
        lastWidthRef.current = next
        return
      }
      /*
        ⚠️ A FEW PIXELS IS NOT A RESIZE WORTH ACTING ON.

        This was exact equality, so a ONE-PIXEL difference re-tiled (and, before that,
        remounted) the map. That is half of the draw-flash bug reported repeatedly:
        filtering the list shrinks it, its scrollbar disappears, this pane gains those
        pixels back. `scrollbarGutter: 'stable'` on the list removes that particular width
        change at its source; this threshold is the general guard, since sub-pixel jitter,
        a scrollbar or a 1px border toggling can all be reintroduced by a change nowhere
        near this file.
      */
      if (Math.abs(next - lastWidthRef.current) < RESIZE_RELAYOUT_MIN_DELTA_PX) return
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
      /*
        Debounced: re-tiling on every frame of an animation is wasteful, and the one that
        matters is the last.

        ⚠️ THE DELAY IS THE LARGEST SINGLE LAG ON THE ANALYTICS PAGE, so it is no longer
        one number (VRG-512). The debounce re-arms on EVERY observed frame, so across the
        sidebar's ~225ms transition the re-tile landed 180ms after the last frame — about
        400ms after the click, long after every other card had settled. This file's own
        comment above already described that symptom for the remount version it replaced:
        "a visible teardown that also arrived ~400ms after every other card had finished
        animating". Swapping remount for relayout removed the flash and kept the tail.

        When the app has ANNOUNCED a chrome transition we do not have to GUESS when the
        motion stops — the announcement bounds it — so the tail only has to outlast the
        gap between two frames, not a whole unknown gesture. Un-announced resizes (a
        window drag, which genuinely has no known end) keep the long debounce.
      */
      const settleMs = isChromeTransitioning()
        ? RELAYOUT_ANNOUNCED_SETTLE_MS
        : RESIZE_RELAYOUT_DEBOUNCE_MS
      resizeTimerRef.current = setTimeout(() => {
        lastWidthRef.current = next
        setRelayoutNonce(n => n + 1)
      }, settleMs)
    })
    observer.observe(el)
    return () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
      observer.disconnect()
    }
    // Re-attached when the container is (re)created — i.e. on the skeleton→ready swap,
    // since the skeleton branch renders a different subtree and drops this ref.
  }, [loading])

  const toggleListCollapsed = useCallback(() => {
    settleOwnResize()
    // Best-effort, close to a no-op now that the collapse ANIMATES: at this exact
    // synchronous point the DOM is still at the OLD width, so Google re-reads a container
    // that has not actually changed size yet. It is a cheap safety net for the
    // (should-never-happen) case where the transition-end signal below does not fire —
    // the REAL, correctly-sized signal is `handleListWidthTransitionEnd`.
    setRelayoutNonce(n => n + 1)
    setListCollapsed(c => !c)
  }, [settleOwnResize])

  /**
   * The collapse/expand ANIMATION genuinely finishing (`ResizableSplit`'s
   * `onLeftWidthTransitionEnd`). Unlike the immediate bump above, the container has by
   * construction reached its new width by the time this fires, so this is the signal that
   * matters: it re-baselines the width observer (a multi-frame CSS transition can trip it
   * several times mid-flight) and re-triggers layout with the real, settled width.
   */
  const handleListWidthTransitionEnd = useCallback(() => {
    settleOwnResize()
    setRelayoutNonce(n => n + 1)
  }, [settleOwnResize])

  const handleResizeCommit = useCallback(() => {
    settleOwnResize()
    setRelayoutNonce(n => n + 1)
  }, [settleOwnResize])

  // One bordered, rounded, clipped container around BOTH panes. The radius is what gives
  // the map corners — a Google Maps canvas has none of its own, so it would otherwise sit
  // as a hard-edged rectangle inside a rounded card.
  //
  // Typed as `SxProps<Theme>`, NOT `as const`: a const-asserted object literal is deeply
  // readonly, and MUI's `sx` overloads reject a readonly array/record (the failure reads
  // as "Index signature for type 'string' is missing in type 'readonly string[]'", which
  // names nothing you wrote).
  const splitSx: SxProps<Theme> = {
    height,
    display: 'flex',
    minHeight: 0,
    border: `1px solid ${theme.palette.primary[100]}`,
    borderRadius: 3,
    overflow: 'hidden',
  }

  // `hideScrollbar` is declared `SxProps<Theme>` in @repo/ui, which is a UNION that
  // includes arrays and theme callbacks — so spreading it straight into an `sx` object
  // literal widens every sibling key into that union and blows MUI's `sx` overloads.
  // Narrowing it once here is what keeps this file off the error list.
  const listSx: SxProps<Theme> = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    /*
      ⚠️ RESERVE THE SCROLLBAR'S SPACE — half the fix for the map flashing and jumping
      when a filter is applied or cleared.

      Fewer rows means no scrollbar, the list narrows by the scrollbar's width, and the
      MAP PANE BESIDE IT widens by the same few pixels — which trips the width observer
      above. `stable` keeps the gutter reserved whether or not the scrollbar is present, so
      a filter cannot change this pane's width at all. It fixes the CAUSE; the observer's
      threshold is the belt-and-braces half.

      Still needed despite `hideScrollbar`: that hides the scrollbar's APPEARANCE, it does
      not stop the browser reserving or releasing its layout space.
    */
    scrollbarGutter: 'stable',
    ...(hideScrollbar as Record<string, unknown>),
  }

  const collapseLabel = `Hide the ${noun} list`
  const expandLabel = `Show the ${noun} list`

  /**
   * The list's own header strip: a count on the left, the collapse control on the right.
   *
   * The control needs a home that is unambiguously PART OF THE LIST, and there was none:
   * a card's header belongs to the shell, and floating a button over the first row would
   * sit on top of the content it hides. A 37px strip is the smallest thing that reads as
   * "this panel's chrome".
   *
   * The count is not filler. It is the one fact the list cannot show while it is scrolled
   * (how much there is), and it is the thing the collapse takes away.
   */
  const listHeader = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        flexShrink: 0,
        pl: 4,
        pr: 2,
        py: 2,
        borderBottom: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      <Typography variant="body3" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {count} {count === 1 ? noun : `${noun}s`}
      </Typography>
      {/* §38: this is a PANEL-STATE control, not a set-removal — the rows are not being
          discarded, the panel is being folded away — so it is a panel-collapse chevron and
          never an `X`. Matched to the Components panel's own collapse control (§28), which
          is `PanelRightCloseIcon` for a right-docked panel; this list is docked LEFT, so it
          is the left-hand pair. */}
      <Tooltip title={collapseLabel}>
        <IconButton
          size="small"
          onClick={toggleListCollapsed}
          aria-label={collapseLabel}
          aria-expanded
          aria-controls={listRegionId}
        >
          <PanelLeftCloseIcon strokeWidth={1} size={18} />
        </IconButton>
      </Tooltip>
    </Stack>
  )

  const listPane = listCollapsed ? (
    <Stack
      alignItems="center"
      sx={{
        height: '100%',
        pt: 2,
        // The rail carries the seam itself: with the list collapsed the resizer is
        // disabled and therefore not rendered, so there is no separator to draw it.
        borderRight: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      <Tooltip title={expandLabel} placement="right">
        <IconButton
          size="small"
          onClick={toggleListCollapsed}
          aria-label={expandLabel}
          aria-expanded={false}
          aria-controls={listRegionId}
        >
          <PanelLeftOpenIcon strokeWidth={1} size={18} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : (
    <Stack id={listRegionId} sx={{ height: '100%', minHeight: 0 }}>
      {listHeader}
      <Box ref={listRef} sx={listSx}>
        {list}
      </Box>
    </Stack>
  )

  // The map's own canvas height. `-2` accounts for the container's 1px border top and
  // bottom so the canvas cannot overflow the rounded clip; a percentage height is already
  // relative to the clipped box, so it is passed through untouched.
  const mapHeight = typeof height === 'number' ? height - 2 : '100%'

  return (
    <Box id={id} sx={{ height: '100%', minHeight: 0 }}>
      {loading ? (
        // Footprint parity (§19): the same bordered split at the same height, with a
        // column of row-shaped placeholders where the rows land and a single block where
        // the map lands — not one generic rectangle over the whole panel.
        <Box sx={splitSx}>
          <Stack
            spacing={0}
            sx={{
              width: defaultListWidth,
              minWidth: defaultListWidth,
              height: '100%',
              overflow: 'hidden',
              borderRight: `1px solid ${theme.palette.primary[100]}`,
            }}
          >
            {/* The header strip is part of the loaded footprint, so it is part of the
                skeleton too (§19) — otherwise the rows jump 37px down on load. */}
            <Box
              sx={{
                flexShrink: 0,
                px: 4,
                py: 2,
                borderBottom: `1px solid ${theme.palette.primary[100]}`,
              }}
            >
              <Skeleton variant="text" width={72} height={16} />
            </Box>
            {Array.from({ length: 8 }).map((_, i) => (
              <Stack key={i} direction="row" spacing={3} alignItems="center" sx={{ py: 3, px: 4 }}>
                <Skeleton variant="rounded" width={44} height={44} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="text" width="70%" height={14} />
                  <Box sx={{ mt: 1 }}>
                    <Skeleton variant="text" width="45%" height={12} />
                  </Box>
                </Box>
              </Stack>
            ))}
          </Stack>
          <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        </Box>
      ) : (
        <ResizableSplit
          sx={splitSx}
          // px, not pct — see SCAN_MAP_LIST_WIDTH. Collapsed, the left column IS the rail,
          // and the resizer is disabled because there is nothing meaningful to size.
          unit="px"
          leftSize={listCollapsed ? LIST_RAIL_WIDTH : listWidth}
          onLeftSizeChange={setListWidth}
          onDragEnd={handleResizeCommit}
          min={LIST_MIN_WIDTH}
          max={LIST_MAX_WIDTH}
          variant="flush"
          disabled={listCollapsed}
          label={`Resize the ${noun} list`}
          // Animate the collapse/expand, not a drag — `ResizableSplit` self-suppresses the
          // transition while its own gesture is live, so this can be unconditionally on.
          animateWidth
          onLeftWidthTransitionEnd={handleListWidthTransitionEnd}
          left={listPane}
          right={
            <Box ref={mapBoxRef} sx={{ flex: 1, minWidth: 0, height: '100%', position: 'relative' }}>
              {/* Rendered OUTSIDE `<Map>` so nothing about the map's lifecycle can tear a
                  control down mid-gesture. */}
              {canvasTopLeft}
              {showRenderModeToggle && (
                // Top-right: `overlayPosition="bottom-left"` below anchors
                // `MapZoomControls` bottom-right, leaving top-right free; a caller's own
                // control (`canvasTopLeft`) owns top-left.
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                  <Box
                    sx={{
                      /*
                        40, matching the Journey chip on the certificate map (a shared
                        `Button`) so the two canvas chips are one family. The inner
                        `ViewToggle` is 32 (`MAP_CHIP_CONTROL_HEIGHT`), so `alignItems:
                        center` leaves its hover wash a clear inset top and bottom.

                        The inset is produced by CENTRING, not by `py`: this box is
                        border-box with a 1px border, so a fixed 40 plus vertical padding
                        would shrink the content box below 32 and reintroduce the very
                        overflow being fixed. Padding and a fixed height cannot both be
                        authoritative here.
                      */
                      height: MAP_CHIP_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      /*
                        `2` (8px), matching the shared `Button`'s own radius rather than
                        the 10px this chip used to carry. The Journey chip beside it IS a
                        shared `Button`, so a hardcoded 10 here left the two canvas chips
                        agreeing on height and disagreeing on corners. The design system's
                        value wins over the one-off; do not restore the 10.
                      */
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.primary[100]}`,
                      boxShadow: theme.shadows[1],
                    }}
                  >
                    <ScanMapRenderModeToggle
                      value={renderMode}
                      onChange={setRenderMode}
                      /*
                        The FILTERED count, so heat's low-volume guard measures what would
                        actually be aggregated. Narrowing to a status with 40 points must be
                        able to say "not enough to be useful" rather than silently drawing a
                        near-empty surface because the unfiltered set cleared the bar.
                      */
                      pointCount={items.length}
                      unavailable={heatUnavailable}
                    />
                  </Box>
                </Box>
              )}
              <Map
                // ⚠️ DELIBERATELY UNKEYED — see the remount note above.
                relayoutNonce={relayoutNonce}
                items={items}
                activeItemId={activeId}
                onMarkerClick={handleMarkerClick}
                onDeselect={handleDeselect}
                apiKey={apiKey}
                mapStyle={mapStyle}
                height={mapHeight}
                defaultZoom={2}
                enableClustering={enableClustering}
                gestureHandling="greedy"
                showLoadingState
                showErrorState
                errorMessage={errorMessage}
                emptyMessage={emptyMessage}
                overlayPosition="bottom-left"
                renderMode={renderMode}
                onHeatUnavailable={handleHeatUnavailable}
                fitToItems
                fitSignature={fitSignature}
                suppressFit={suppressFit}
                path={path}
                drawMode={drawMode}
                drawnRing={drawnRing}
                onDrawComplete={onDrawComplete}
                onDrawCancel={onDrawCancel}
              />
            </Box>
          }
        />
      )}
    </Box>
  )
}
